import type { Currency } from "~/api/products/types";
import { logger } from "~/services/logger";

import { DEFAULT_LOCALE } from "./constants";

type PriceFormatterArgs = {
  value: number | string;
  currency: Currency;
  locale?: string | null;
  options?: {
    showDecimals?: boolean;
    hideCurrency?: boolean;
    signDisplay?: Intl.NumberFormatOptions["signDisplay"];
  };
};

export const priceFormatter = ({
  value,
  currency,
  locale,
  options
}: PriceFormatterArgs) => {
  /** Sometimes happens that the value is directly a string */
  if (typeof value === "string") return value;

  /** Convert from unit value to display value (e.g. "cents" to "euro") */
  const priceCurrency = value / 100;

  let decimals;
  if (options?.showDecimals) {
    decimals = 2;
  } else if (priceCurrency % 1 === 0) {
    decimals = 0;
  } else {
    decimals = 2;
  }

  /** Format the price with safe locale */
  const safeLocale = getSafeLocale(locale);
  const price = new Intl.NumberFormat(safeLocale, {
    style: options?.hideCurrency ? "decimal" : "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: options?.signDisplay
  }).format(priceCurrency);

  return price;
};

/**
 * Gets a safe locale for Intl.NumberFormat, falling back to default if invalid
 */
function getSafeLocale(locale: string | null | undefined): string {
  if (!locale || typeof locale !== "string" || locale.trim() === "") {
    return DEFAULT_LOCALE;
  }

  if (isValidLocale(locale.trim())) {
    return locale;
  }

  return DEFAULT_LOCALE;
}

/**
 * Validates if a locale string is supported by Intl.NumberFormat
 */
function isValidLocale(locale: string): boolean {
  try {
    const supportedLocales = Intl.NumberFormat.supportedLocalesOf([locale]);
    if (supportedLocales.length === 0) {
      throw new Error("Supported locales is empty");
    }
  } catch (error) {
    logger.warn(`PriceFormatter: Invalid locale: ${locale}`, {
      locale,
      error
    });
    return false;
  }

  return true;
}
